import { Dialog, DialogContent, DialogActions, DialogTitle } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import mime from "mime";

import PrimaryButton from "./primary_button";
import ProgressBar from "./progress_bar";
import DynamicIcon from "./dynamic_icon";

function base64ToBlob(base64: string, contentType: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

/**
 * CustomModalLoading
 */
const CustomModalLoading = ({
  loading,
  message,
}: { loading: boolean, message?: string }) => {
  return (
    <>
      {
        loading &&
        <div className="absolute size-full z-50 top-0 left-0 flex justify-center items-center">
          <div className="flex flex-col w-48 p-4 rounded-lg gap-2 border border-stone-500 justify-center items-center bg-[#FFFFFF] dark:bg-[#393939]">
            <div className="w-24">
              <img src={`images/mini-logo.png`} alt="logo gdol" />
            </div>
            <div className="w-full">
              <ProgressBar />
              <p className="text-black dark:text-white text-center">{message ?? "Salvando"}</p>
            </div>
          </div>
        </div>
      }
    </>
  );
}

/**
 * ImageViewerModal
 */



type Props = {
  open: boolean;
  saving: boolean;
  nomeArquivo?: string;
  arquivo?: string;
  cancelButtonLabel?: string;
  savingLabel?: string;
  buttonColor?: "primary-green" | "white" | "secondary" | "secondary-white" | "yellow" | "red";
  leftButtonSlot?: React.ReactNode,
  onClose: () => void;
};

export const ImageViewerModal = ({
  open,
  saving,
  nomeArquivo,
  arquivo,
  cancelButtonLabel,
  savingLabel,
  buttonColor,
  leftButtonSlot,
  onClose,
}: Props) => {
  const [imageUrl, setImageUrl] = useState<string>();
  const mimeType = nomeArquivo ? mime.getType(nomeArquivo) : null;

  useEffect(() => {
    if (!arquivo || !nomeArquivo) {
      setImageUrl(undefined);
      return;
    }

    const url = URL.createObjectURL(
      base64ToBlob(
        arquivo,
        mimeType ?? 'image/*',
      ),
    );
    setImageUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [arquivo, nomeArquivo, mimeType]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
    }
  }, [
    saving,
    onClose,
  ]);

  const handleDownload = useCallback(() => {
    if (!imageUrl || saving) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = nomeArquivo ?? `imagem.${mimeType?.split('/')[1] ?? 'png'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [imageUrl, nomeArquivo, mimeType, saving]);


  return (

    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={true}
      sx={{
        "& .MuiDialog-container": {
          position: 'relative',

          "& .MuiPaper-root": {
            width: '95vw',
            height: '95vh',
          },
          "& .MuiFormControlLabel-label": {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '13px',
          },
          "& .MuiCheckbox-root": {
            padding: '.25rem',
          },
        },
      }}>

      <DialogTitle sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }} component="div">
        <h2>{nomeArquivo}</h2>
        <div
          onClick={e => handleClose()}>
          <DynamicIcon
            nameIcon="Close"
            size={25}
            className="text-black dark:text-white cursor-pointer"
          />
        </div>
      </DialogTitle>

      <DialogContent>
        {
          arquivo ? (
            <>
              {
                (mimeType?.startsWith('image/') || mimeType === 'application/pdf') && imageUrl
                  ? (
                    <div className="size-full flex justify-center items-center bg-white">
                      <iframe
                        src={imageUrl}
                        className="size-full object-contain"
                        title="Arquivo Ocorrência"
                      />
                    </div>
                  ) : (
                    <div className="size-full flex flex-col justify-center items-center">
                      <span className="text-lg font-bold">Este conteudo não pode ser pre-visualizado.</span>
                    </div>
                  )
              }
            </>
          ) : (
            <div className="w-full h-full flex flex-row justify-center items-center">
              <div className="text-black dark:text-white">
                Falha ao gerar relatório.
              </div>
            </div>
          )
        }
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="flex flex-row">
            {leftButtonSlot}
          </div>

          <div className="flex flex-row gap-2 items-center">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                {cancelButtonLabel ?? 'Cancelar'}
              </h1>
            </div>

            <div>
              <div className="flex flex-row gap-2">
                <div className="w-[8rem]">
                  <PrimaryButton
                    disabled={saving}
                    buttonColor={buttonColor ?? "primary-green"}
                    onClick={handleDownload}>
                    Baixar
                  </PrimaryButton>
                </div>

              </div>
            </div>
          </div>
        </div>
      </DialogActions>

      <CustomModalLoading
        loading={saving}
        message={(savingLabel ?? 'Baixando...')} />

    </Dialog>
  );
}